import { z } from 'zod';

/**
 * Telemetry health-score DTOs (RUF-512).
 *
 * Mirrors db/schema/598_telemetry_score_snapshot.sql (telemetry_score_snapshot)
 * and the scoring engine's output shapes (apps/api/src/scoring/engine.ts —
 * FeatureScore / CategoryScore), plus this issue's coverage calc
 * (telemetry-coverage.ts). Three distinct schemas by design so the API layer
 * (RUF-513) cannot accidentally leak feature/model detail to the client:
 *   - telemetryScoreSnapshotSchema — the persisted row (ops + audit).
 *   - telemetryScoreOpsResponseSchema — full ops GET response.
 *   - telemetryScoreClientResponseSchema — client GET projection: overall,
 *     coverage, scoredAt, categories:[{category, score, coverage}] ONLY. No
 *     features, no modelVersion — matches the column-level GRANT in 597
 *     which deliberately omits model_version and features for
 *     client_readonly. scoredAt added in RUF-521 for the FMV call-out's
 *     "scored at" date (not ops-internal, safe to expose).
 *
 * RUF-552 (passport engine, design docs/prds/digital-passport-health-confidence.md
 * D2/D4/D5) adds disclosures / horizonBand / wearTrajectory / confidenceTier
 * (db/schema/604_telemetry_score_snapshot_passport_engine.sql). All four are
 * explicitly client-safe (D2/D4/D5) — unlike features/modelVersion they
 * appear on snapshot, ops, AND client schemas.
 */

export const telemetryDisclosureSchema = z.enum([
  'thermal_stress_exposure',
  'fabric_instability',
  'high_duty_cycle',
]);
export type TelemetryDisclosureDto = z.infer<typeof telemetryDisclosureSchema>;

export const horizonBandSchema = z.enum(['healthy', 'degraded', 'warning', 'critical']);
export type HorizonBandDto = z.infer<typeof horizonBandSchema>;

export const wearTrajectorySchema = z.enum(['stable', 'accelerating']);
export type WearTrajectoryDto = z.infer<typeof wearTrajectorySchema>;

export const confidenceTierSchema = z.enum(['full', 'partial', 'minimal']);
export type ConfidenceTierDto = z.infer<typeof confidenceTierSchema>;

export const telemetryFeatureScoreSchema = z.object({
  feature: z.string(),
  category: z.string(),
  score: z.number(),
  totalFieldWeight: z.number(),
  telemetryRowCount: z.number(),
});
export type TelemetryFeatureScore = z.infer<typeof telemetryFeatureScoreSchema>;

/** Ops-facing category score: score + coverage + featureCount. */
export const telemetryCategoryScoreSchema = z.object({
  category: z.string(),
  score: z.number(),
  coverage: z.coerce.number().min(0).max(1),
  featureCount: z.number(),
});
export type TelemetryCategoryScore = z.infer<typeof telemetryCategoryScoreSchema>;

/**
 * Persisted score snapshot — mirrors telemetry_score_snapshot
 * (db/schema/598_telemetry_score_snapshot.sql +
 * db/schema/600_telemetry_score_snapshot_window.sql) column-for-column.
 *
 * windowStart/windowEnd (RUF-517) stamp the trailing-window rollup range;
 * both are null on pre-RUF-517 rows that scored a single source_day with no
 * window. sourceDay is kept (= windowEnd on window-scored rows) for backward
 * compatibility with existing "latest day scored" consumers.
 */
export const telemetryScoreSnapshotSchema = z.object({
  id: z.string().uuid(),
  assetId: z.string().uuid(),
  scoredAt: z.string(),
  sourceDay: z.string(),
  windowStart: z.string().nullable().optional(),
  windowEnd: z.string().nullable().optional(),
  modelVersion: z.string(),
  triggerId: z.string(),
  overallScore: z.coerce.number().min(0).max(100),
  overallCoverage: z.coerce.number().min(0).max(1),
  categories: z.array(telemetryCategoryScoreSchema),
  features: z.array(telemetryFeatureScoreSchema),
  disclosures: z.array(telemetryDisclosureSchema).nullable().optional(),
  horizonBand: horizonBandSchema.nullable().optional(),
  wearTrajectory: wearTrajectorySchema.nullable().optional(),
  confidenceTier: confidenceTierSchema.nullable().optional(),
});
export type TelemetryScoreSnapshot = z.infer<typeof telemetryScoreSnapshotSchema>;

/**
 * Ops GET response — full detail: categories (with coverage), features,
 * model_version, source_day, window_start/window_end (RUF-517). For
 * analyst/admin consumption only.
 */
export const telemetryScoreOpsResponseSchema = z.object({
  assetId: z.string().uuid(),
  scoredAt: z.string(),
  sourceDay: z.string(),
  windowStart: z.string().nullable().optional(),
  windowEnd: z.string().nullable().optional(),
  modelVersion: z.string(),
  triggerId: z.string(),
  overallScore: z.coerce.number().min(0).max(100),
  overallCoverage: z.coerce.number().min(0).max(1),
  categories: z.array(telemetryCategoryScoreSchema),
  features: z.array(telemetryFeatureScoreSchema),
  disclosures: z.array(telemetryDisclosureSchema).nullable().optional(),
  horizonBand: horizonBandSchema.nullable().optional(),
  wearTrajectory: wearTrajectorySchema.nullable().optional(),
  confidenceTier: confidenceTierSchema.nullable().optional(),
});
export type TelemetryScoreOpsResponse = z.infer<typeof telemetryScoreOpsResponseSchema>;

/** Client-facing category projection — score + coverage only, no featureCount. */
export const telemetryClientCategoryScoreSchema = z.object({
  category: z.string(),
  score: z.number(),
  coverage: z.coerce.number().min(0).max(1),
});
export type TelemetryClientCategoryScore = z.infer<typeof telemetryClientCategoryScoreSchema>;

/**
 * Client GET response — deliberately a strict projection: overall, coverage,
 * categories:[{category, score, coverage}]. NO features, NO modelVersion —
 * kept as a distinct schema from the ops response so the API layer can't
 * accidentally widen it and leak ops-internal model/feature detail.
 */
export const telemetryScoreClientResponseSchema = z.object({
  overall: z.coerce.number().min(0).max(100),
  coverage: z.coerce.number().min(0).max(1),
  scoredAt: z.string(),
  categories: z.array(telemetryClientCategoryScoreSchema),
  // RUF-552 — disclosures/horizonBand/wearTrajectory/confidenceTier are
  // explicitly client-safe (D2/D4/D5), unlike features/modelVersion above.
  disclosures: z.array(telemetryDisclosureSchema).nullable().optional(),
  horizonBand: horizonBandSchema.nullable().optional(),
  wearTrajectory: wearTrajectorySchema.nullable().optional(),
  confidenceTier: confidenceTierSchema.nullable().optional(),
});
export type TelemetryScoreClientResponse = z.infer<typeof telemetryScoreClientResponseSchema>;

/**
 * Score history (RUF-518) — trend view on the asset detail, both portals.
 * Mirrors the FMV snapshot-history pattern: a lightweight per-run
 * projection (no `features`; ops still gets `modelVersion`/`triggerId` for
 * the tooltip) capped at 50 runs. Two distinct shapes for the same reason
 * the latest-score responses are split — the client history item can never
 * carry ops-internal categoryScore.featureCount.
 */
export const telemetryScoreHistoryOpsItemSchema = z.object({
  scoredAt: z.string(),
  sourceDay: z.string(),
  modelVersion: z.string(),
  triggerId: z.string(),
  overallScore: z.coerce.number().min(0).max(100),
  overallCoverage: z.coerce.number().min(0).max(1),
  categories: z.array(telemetryCategoryScoreSchema),
  disclosures: z.array(telemetryDisclosureSchema).nullable().optional(),
  horizonBand: horizonBandSchema.nullable().optional(),
  wearTrajectory: wearTrajectorySchema.nullable().optional(),
  confidenceTier: confidenceTierSchema.nullable().optional(),
});
export type TelemetryScoreHistoryOpsItem = z.infer<typeof telemetryScoreHistoryOpsItemSchema>;

export const telemetryScoreHistoryOpsResponseSchema = z.object({
  data: z.array(telemetryScoreHistoryOpsItemSchema),
});
export type TelemetryScoreHistoryOpsResponse = z.infer<typeof telemetryScoreHistoryOpsResponseSchema>;

export const telemetryScoreHistoryClientItemSchema = z.object({
  scoredAt: z.string(),
  overallScore: z.coerce.number().min(0).max(100),
  overallCoverage: z.coerce.number().min(0).max(1),
  categories: z.array(telemetryClientCategoryScoreSchema),
  disclosures: z.array(telemetryDisclosureSchema).nullable().optional(),
  horizonBand: horizonBandSchema.nullable().optional(),
  wearTrajectory: wearTrajectorySchema.nullable().optional(),
  confidenceTier: confidenceTierSchema.nullable().optional(),
});
export type TelemetryScoreHistoryClientItem = z.infer<typeof telemetryScoreHistoryClientItemSchema>;

export const telemetryScoreHistoryClientResponseSchema = z.object({
  data: z.array(telemetryScoreHistoryClientItemSchema),
});
export type TelemetryScoreHistoryClientResponse = z.infer<
  typeof telemetryScoreHistoryClientResponseSchema
>;
